import React, { useState, useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);
const EMPTY = { configured: false, enabled: true, rating: null, total: null, maps_url: '', reviews: [] };

export const useGoogleReviews = () => {
  const [data, setData] = useState(EMPTY);
  useEffect(() => { axios.get(`${API}/reviews/public`).then(r => setData(r.data || EMPTY)).catch(() => {}); }, []);
  return data;
};

const Stars = ({ n = 5, size = 16 }) => (
  <div className="flex" aria-label={`${n} stars`}>
    {[...Array(5)].map((_, i) => <Star key={i} size={size} fill={i < Math.round(n) ? '#FBBC05' : 'none'} className={i < Math.round(n) ? 'text-[#FBBC05]' : 'text-gray-300'} />)}
  </div>
);

const Avatar = ({ r }) => {
  const [err, setErr] = useState(false);
  const initials = (r.author || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return r.photo_url && !err
    ? <img src={absUrl(r.photo_url)} alt={r.author} referrerPolicy="no-referrer" onError={() => setErr(true)} className="w-11 h-11 rounded-full object-cover border-2 border-white shadow" />
    : <div className="w-11 h-11 rounded-full bg-[#1a5c3a] text-white flex items-center justify-center font-semibold text-sm border-2 border-white shadow">{initials}</div>;
};

export const GoogleRatingBadge = ({ language, data }) => {
  const pt = language === 'pt';
  const live = data.rating != null;
  return (
    <div data-testid="google-rating-badge">
      <div className="flex items-center gap-2">
        <span className="text-3xl font-bold text-gray-900" data-testid="google-rating-value">{live ? Number(data.rating).toFixed(1) : '5.0'}</span>
        <Stars n={live ? data.rating : 5} size={18} />
      </div>
      {live && data.total != null && <p className="text-xs text-gray-500 mt-0.5" data-testid="google-rating-total">{data.total} {pt ? 'avaliações no Google' : 'Google reviews'}</p>}
    </div>
  );
};

export const ReviewsCounter = ({ language, data }) => {
  const pt = language === 'pt';
  const live = data.rating != null && data.total != null;
  const faces = (data.reviews || []).filter(r => r.photo_url).slice(0, 3);
  return (
    <div className="mt-8 flex items-center gap-3" data-testid="hero-reviews-counter">
      <div className="flex -space-x-3">
        {faces.length > 0 ? faces.map((r, i) => <img key={i} src={absUrl(r.photo_url)} alt={r.author} referrerPolicy="no-referrer" className="w-10 h-10 rounded-full border-2 border-white object-cover bg-white" />)
          : [...Array(3)].map((_, i) => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-[#1a5c3a] text-white flex items-center justify-center"><Star size={14} fill="#FBBC05" className="text-[#FBBC05]" /></div>)}
      </div>
      <a href={data.maps_url || 'https://g.page/r/CZuJ-aERsocTEAE/review'} target="_blank" rel="noopener noreferrer" className="bg-[#d4a84b] text-white px-4 py-2 rounded-lg hover:bg-[#c4983b] transition-colors" data-testid="hero-reviews-link">
        {live ? (
          <><span className="font-bold text-lg">{Number(data.rating).toFixed(1)}★</span><span className="text-sm ml-1">· {data.total} {pt ? 'avaliações no Google' : 'Google reviews'}</span></>
        ) : (
          <><span className="font-bold text-lg">50+</span><span className="text-sm ml-1">{pt ? 'Avaliações e Crescendo' : 'Reviews & Growing'}</span></>
        )}
      </a>
    </div>
  );
};

export const GoogleReviewsGrid = ({ language, data, fallback = [] }) => {
  const pt = language === 'pt';
  const reviews = data.reviews || [];
  if (reviews.length === 0) {
    return fallback.map((t, i) => (
      <div key={i} className="bg-white p-6 rounded-xl shadow-sm" data-testid="review-card-fallback">
        <div className="text-4xl text-[#d4a84b] mb-3">&ldquo;</div>
        <p className="text-gray-700 mb-4 italic">{t.text}</p>
        <p className="text-sm font-semibold text-gray-900">– {t.author}</p>
      </div>
    ));
  }
  return (
    <>
      {reviews.map((r) => (
        <div key={r.id} className="bg-white p-5 rounded-xl shadow-sm flex flex-col" data-testid={`review-card-${r.source}`}>
          <div className="flex items-center gap-3 mb-3">
            <Avatar r={r} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{r.author}</p>
              <p className="text-[11px] text-gray-400">{r.relative_time || r.date || ''}</p>
            </div>
            {r.source === 'google' && (
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-label="Google"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            )}
          </div>
          <Stars n={r.rating || 5} size={14} />
          <p className="text-gray-700 text-sm mt-3 leading-relaxed line-clamp-6">{r.text}</p>
          {r.author_url && <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="mt-auto pt-3 text-[11px] text-gray-400 hover:text-[#1a5c3a] inline-flex items-center gap-1">{pt ? 'Ver no Google' : 'View on Google'} <ExternalLink size={10} /></a>}
        </div>
      ))}
      {data.maps_url && (
        <a href={data.maps_url} target="_blank" rel="noopener noreferrer" className="col-span-full text-center text-sm text-[#1a5c3a] font-medium hover:underline inline-flex items-center justify-center gap-1" data-testid="reviews-see-all">
          {pt ? `Ver todas as ${data.total || ''} avaliações no Google` : `See all ${data.total || ''} reviews on Google`} <ExternalLink size={14} />
        </a>
      )}
    </>
  );
};

export default GoogleReviewsGrid;
